import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import { getSocket } from '../socket/client.js'
import { useConversationsStore } from '../store/conversationsStore.js'
import type { Message, MessageAttachment } from '../modules/conversations/types.js'

export interface LiveChat {
  id: string
  visitorId: string
  visitorName: string | null
  visitorEmail: string | null
  clientId: string | null
  assignedAgentId: string | null
  aiAssigned?: boolean
  aiAgentName?: string | null
  status: 'active' | 'finalized' | 'abandoned'
  lastMessageText: string | null
  lastMessageAt: string
  lastMessageDirection: 'inbound' | 'outbound'
  isRead: boolean
  conversationId: string | null
  createdAt: string
  updatedAt: string
}

interface ApiLiveChat {
  id: string
  visitor_id: string
  visitor_name: string | null
  visitor_email: string | null
  client_id: string | null
  assigned_agent_id: string | null
  ai_assigned?: boolean
  ai_agent_name?: string | null
  status: 'active' | 'finalized' | 'abandoned'
  last_message_text: string | null
  last_message_at: string
  last_message_direction: 'inbound' | 'outbound'
  is_read: boolean
  conversation_id?: string | null
  created_at: string
  updated_at: string
}

function toLiveChat(c: ApiLiveChat): LiveChat {
  return {
    id: c.id,
    visitorId: c.visitor_id,
    visitorName: c.visitor_name,
    visitorEmail: c.visitor_email,
    clientId: c.client_id,
    assignedAgentId: c.assigned_agent_id,
    aiAssigned: c.ai_assigned ?? false,
    aiAgentName: c.ai_agent_name ?? null,
    status: c.status,
    lastMessageText: c.last_message_text,
    lastMessageAt: c.last_message_at,
    lastMessageDirection: c.last_message_direction,
    isRead: c.is_read,
    conversationId: c.conversation_id ?? null,
    createdAt: c.created_at,
    updatedAt: c.updated_at,
  }
}

interface ApiLiveChatMessage {
  id: string
  chat_id: string
  direction: 'inbound' | 'outbound'
  sender_handle: string | null
  sender_agent_id: string | null
  conversation_id?: string | null
  content_text: string | null
  attachments: MessageAttachment[]
  status: string
  sent_at: string
  created_at: string
}

interface ApiLiveChatConversation {
  id: string
  reference_id: string | null
  subject: string | null
  opened_at: string
}

export interface LiveChatConversation {
  id: string
  referenceId: string | null
  subject: string | null
  openedAt: string
}

function toMessage(m: ApiLiveChatMessage, visitorName: string | null): Message {
  return {
    id: m.id,
    conversationId: m.conversation_id ?? m.chat_id,
    direction: m.direction,
    senderHandle: m.direction === 'inbound' ? null : m.sender_handle,
    senderAgentId: m.sender_agent_id,
    senderAgentName: null,
    senderName: m.direction === 'inbound' ? visitorName : null,
    body: m.content_text ?? '',
    attachments: m.attachments ?? [],
    status: m.status as Message['status'],
    createdAt: m.sent_at,
  }
}

/**
 * The Live Chat sidebar list — active (not-yet-finalized) chats. Driven by
 * the `live:chat:list:bump` socket event (emitted on new message, escalation
 * assigned, and finalize) instead of polling. An initial fetch on mount
 * backfills the list for page load / refresh; a debounced refetch on each
 * bump keeps it live without spamming the API on rapid message bursts.
 * useLiveChat(id) below is the per-thread socket-driven hook for the open
 * conversation.
 */
export interface LiveChatFilters {
  status?: 'all' | 'unassigned' | 'mine' | 'unread'
  search?: string | undefined
  page?: number
  limit?: number
}

function liveChatQuery(filters?: LiveChatFilters): string {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.search) params.set('search', filters.search)
  if (filters?.page) params.set('page', String(filters.page))
  if (filters?.limit) params.set('limit', String(filters.limit))
  return params.toString()
}

export function useLiveChats(filters?: LiveChatFilters) {
  const [chats, setChats] = useState<LiveChat[]>([])
  const [loading, setLoading] = useState(true)

  const fetchChats = useCallback(async () => {
    try {
      const qs = liveChatQuery(filters)
      const res = await api.get<{ chats: ApiLiveChat[] }>(`/agent/live-chats${qs ? `?${qs}` : ''}`)
      setChats((res.chats ?? []).map(toLiveChat))
    } catch {
      // ignore — next bump retries
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void fetchChats()
    const socket = getSocket()
    let timer: ReturnType<typeof setTimeout> | null = null
    const onBump = (): void => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => void fetchChats(), 400)
    }
    socket.on('live:chat:list:bump', onBump)
    socket.on('live:chat:resumed', onBump)
    socket.on('live:chat:ended', onBump)
    window.addEventListener('kc3:chat:resumed', onBump)
    window.addEventListener('kc3:chat:ended', onBump)
    return () => {
      if (timer) clearTimeout(timer)
      socket.off('live:chat:list:bump', onBump)
      socket.off('live:chat:resumed', onBump)
      socket.off('live:chat:ended', onBump)
      window.removeEventListener('kc3:chat:resumed', onBump)
      window.removeEventListener('kc3:chat:ended', onBump)
    }
  }, [fetchChats])

  return { chats, loading, fetchChats }
}

export interface UseLiveChatReturn {
  chat: LiveChat | null
  messages: Message[]
  conversations: LiveChatConversation[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  error: string | null
  appendMessage: (message: Message) => void
  replaceMessage: (oldId: string, newMessage: Message) => void
  markMessageFailed: (messageId: string, error: string) => void
  patchMessage: (messageId: string, patch: Partial<Message>) => void
  refetch: () => Promise<void>
  loadMore: () => Promise<void>
  updateStatus: (status: LiveChat['status']) => void
}

/** A single live chat's detail + messages, for the open thread — fetched
 *  once on selection, then kept live via the same conversation:message:new
 *  socket events the finalized-conversation thread uses (the ws-gateway
 *  resolves a live_chats id through the same event, see server.ts). */
export function useLiveChat(chatId: string | null): UseLiveChatReturn {
  const [chat, setChat] = useState<LiveChat | null>(null)
  const [conversations, setConversations] = useState<LiveChatConversation[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const beforeRef = useRef<string | null>(null)

  const messages = useConversationsStore((s) =>
    chatId !== null ? (s.messagesByConversationId[chatId] ?? EMPTY_MESSAGES) : EMPTY_MESSAGES,
  )
  const setMessages = useConversationsStore((s) => s.setMessages)
  const prependStoreMessages = useConversationsStore((s) => s.prependMessages)
  const appendStoreMessage = useConversationsStore((s) => s.appendMessage)
  const replaceStoreMessage = useConversationsStore((s) => s.replaceMessage)
  const markStoreMessageFailed = useConversationsStore((s) => s.markMessageFailed)
  const patchStoreMessage = useConversationsStore((s) => s.patchMessage)

  const doFetch = useCallback(
    async (id: string): Promise<void> => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      setLoading(true)
      setLoadingMore(false)
      setHasMore(false)
      beforeRef.current = null
      setError(null)
      try {
        const resp = await api.get<{
          chat: ApiLiveChat
          messages: ApiLiveChatMessage[]
          conversations: ApiLiveChatConversation[]
          hasMore: boolean
          before?: string
        }>(`/agent/live-chats/${id}?limit=20`)
        if (ctrl.signal.aborted) return
        setChat(toLiveChat(resp.chat))
        setConversations(
          (resp.conversations ?? []).map((c) => ({
            id: c.id,
            referenceId: c.reference_id,
            subject: c.subject,
            openedAt: c.opened_at,
          })),
        )
        setMessages(id, (resp.messages ?? []).map((m) => toMessage(m, resp.chat.visitor_name)))
        setHasMore(resp.hasMore)
        beforeRef.current = resp.before ?? null
      } catch (err) {
        if (ctrl.signal.aborted) return
        setError(`Failed to load chat: ${(err as Error).message}`)
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    },
    [setMessages],
  )

  useEffect(() => {
    if (chatId === null) {
      setChat(null)
      setConversations([])
      setError(null)
      return
    }
    void doFetch(chatId)
    return () => abortRef.current?.abort()
  }, [chatId, doFetch])

  // Real-time lifecycle listener: when chat is ended, immediately flip chat.status
  // to 'abandoned' so the message composer and actions vanish with zero lag.
  // When a bump arrives for this chat (e.g. chat resumed or reassigned), re-fetch.
  useEffect(() => {
    if (chatId === null) return
    const socket = getSocket()

    const onChatEnded = (data: { chatId: string; status?: string }): void => {
      if (data?.chatId === chatId) {
        setChat((prev) => (prev ? { ...prev, status: 'abandoned' } : null))
      }
    }

    const onLocalEnded = (e: Event): void => {
      const detail = (e as CustomEvent<{ chatId?: string }>).detail
      if (detail?.chatId === chatId) {
        setChat((prev) => (prev ? { ...prev, status: 'abandoned' } : null))
      }
    }

    const onChatResumed = (data: { chatId?: string | undefined; assignedAgentId?: string | undefined }): void => {
      if (data?.chatId === chatId) {
        setChat((prev) =>
          prev
            ? {
                ...prev,
                status: 'active',
                ...(data.assignedAgentId ? { assignedAgentId: data.assignedAgentId } : {}),
              }
            : null,
        )
        void doFetch(chatId)
      }
    }

    const onLocalResumed = (e: Event): void => {
      const detail = (e as CustomEvent<{ chatId?: string | undefined; assignedAgentId?: string | undefined }>).detail
      if (detail?.chatId === chatId) {
        setChat((prev) =>
          prev
            ? {
                ...prev,
                status: 'active',
                ...(detail.assignedAgentId ? { assignedAgentId: detail.assignedAgentId } : {}),
              }
            : null,
        )
        void doFetch(chatId)
      }
    }

    const onOfferAccepted = (e: { sessionId?: string; assignedAgentId?: string }): void => {
      if (e?.sessionId === chatId) {
        onChatResumed({
          chatId: e.sessionId,
          ...(e.assignedAgentId ? { assignedAgentId: e.assignedAgentId } : {}),
        })
      }
    }

    const onBump = (data: { chatId?: string }): void => {
      if (data?.chatId === chatId) {
        void doFetch(chatId)
      }
    }

    socket.on('live:chat:ended', onChatEnded)
    socket.on('live:chat:resumed', onChatResumed)
    socket.on('chat:offer:accepted', onOfferAccepted)
    socket.on('live:chat:list:bump', onBump)
    window.addEventListener('kc3:chat:ended', onLocalEnded)
    window.addEventListener('kc3:chat:resumed', onLocalResumed)

    return () => {
      socket.off('live:chat:ended', onChatEnded)
      socket.off('live:chat:resumed', onChatResumed)
      socket.off('chat:offer:accepted', onOfferAccepted)
      socket.off('live:chat:list:bump', onBump)
      window.removeEventListener('kc3:chat:ended', onLocalEnded)
      window.removeEventListener('kc3:chat:resumed', onLocalResumed)
    }
  }, [chatId, doFetch])

  const updateStatus = useCallback((status: LiveChat['status']) => {
    setChat((prev) => (prev ? { ...prev, status } : null))
  }, [])

  const appendMessage = useCallback(
    (m: Message) => {
      if (chatId) appendStoreMessage(chatId, m)
    },
    [chatId, appendStoreMessage],
  )
  const replaceMessage = useCallback(
    (oldId: string, newM: Message) => {
      if (chatId) replaceStoreMessage(chatId, oldId, newM)
    },
    [chatId, replaceStoreMessage],
  )
  const markMessageFailed = useCallback(
    (messageId: string, err: string) => {
      if (chatId) markStoreMessageFailed(chatId, messageId, err)
    },
    [chatId, markStoreMessageFailed],
  )
  const patchMessage = useCallback(
    (messageId: string, patch: Partial<Message>) => {
      if (chatId) patchStoreMessage(chatId, messageId, patch)
    },
    [chatId, patchStoreMessage],
  )
  const refetch = useCallback(async (): Promise<void> => {
    if (chatId !== null) await doFetch(chatId)
  }, [chatId, doFetch])

  const loadMore = useCallback(async (): Promise<void> => {
    if (chatId === null || beforeRef.current === null || !hasMore || loadingMore) return
    setLoadingMore(true)
    try {
      const resp = await api.get<{
        chat: ApiLiveChat
        messages: ApiLiveChatMessage[]
        conversations: ApiLiveChatConversation[]
        hasMore: boolean
        before?: string
      }>(`/agent/live-chats/${chatId}?before=${encodeURIComponent(beforeRef.current)}&limit=20`)
      const mapped = (resp.messages ?? []).map((m) => toMessage(m, resp.chat.visitor_name))
      prependStoreMessages(chatId, mapped)
      setHasMore(resp.hasMore)
      beforeRef.current = resp.before ?? null
    } catch {
      // keep existing messages; do not surface an error for an optional history load
    } finally {
      setLoadingMore(false)
    }
  }, [chatId, hasMore, loadingMore, prependStoreMessages])

  return { chat, messages, conversations, loading, loadingMore, hasMore, error, appendMessage, replaceMessage, markMessageFailed, patchMessage, refetch, loadMore, updateStatus }
}

export interface LiveChatCounts {
  all: number
  unassigned: number
  mine: number
  unread: number
}

export function useLiveChatCounts() {
  const [counts, setCounts] = useState<LiveChatCounts>({ all: 0, unassigned: 0, mine: 0, unread: 0 })
  const [loading, setLoading] = useState(true)

  const fetchCounts = useCallback(async () => {
    try {
      const res = await api.get<LiveChatCounts>('/agent/live-chats/counts')
      setCounts(res)
    } catch {
      // keep previous counts
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchCounts()
    const socket = getSocket()
    let timer: ReturnType<typeof setTimeout> | null = null
    const onBump = (): void => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => void fetchCounts(), 400)
    }
    socket.on('live:chat:list:bump', onBump)
    return () => {
      if (timer) clearTimeout(timer)
      socket.off('live:chat:list:bump', onBump)
    }
  }, [fetchCounts])

  return { counts, loading, refetch: fetchCounts }
}

const EMPTY_MESSAGES: Message[] = []
